/**
 * App shell — header (streak + title), level selector, section tabs
 * (Flashcards | Quiz | Situations), main content, footer.
 *
 * The "Practice" parent label lives in the header; section tabs render
 * below the level selector. Level selector stays visible across all
 * sections — Situations also filters by CEFR.
 */

import { StudyMode, ToastContainer } from '@kevvykevwin/lang-core/components';

import {
  audioService,
  signals,
  quizRegistry,
  currentSection,
  situationsSignals,
  activeSituationCategory
} from './main.js';
import { streakCount } from './streak.js';
import { ENGLISH_LEVELS } from './types.js';
import type { EnglishCardExt } from './types.js';
import { SituationsBrowser } from './situations/SituationsBrowser.js';
import type { Section } from './situations/types.js';
import { learnerLanguage, L1_META, SELECTABLE_L1S } from './i18n/learner.js';
import { EnglishCardBack } from './cards/EnglishCardBack.js';
import { L1QuizToggle } from './components/L1QuizToggle.js';
import { AccountMenu } from './components/AccountMenu.js';
import { OnboardingHint } from './components/OnboardingHint.js';

function playAudio(text: string): void {
  audioService.playAudio(text).catch(err => {
    console.warn('Audio playback failed:', err);
    signals.showNotification('Could not play audio', 'error');
  });
}

const SECTION_LABELS: Record<Section, string> = {
  flashcards: 'Flashcards',
  quiz: 'Quiz',
  situations: 'Situations'
};

const SECTIONS: readonly Section[] = ['flashcards', 'quiz', 'situations'];

function SectionTabs() {
  const active = currentSection.value;
  return (
    <div className="flex gap-2 mb-6 justify-center" role="tablist" aria-label="Practice section">
      {SECTIONS.map(s => (
        <button
          key={s}
          type="button"
          role="tab"
          aria-selected={active === s}
          onClick={() => {
            // Idempotent: clicking the active tab is a no-op. Without this
            // guard, a stray double-click on Flashcards/Quiz would call
            // signals.setMode again and reset cardIndex/isFlipped/
            // quizAnswered (per lang-core signals.js setMode semantics).
            if (currentSection.value === s) return;
            currentSection.value = s;
            // Sync main signals' currentMode for the Flashcards/Quiz
            // sections so existing lang-core wiring continues to work.
            if (s === 'flashcards') signals.setMode('study');
            else if (s === 'quiz') signals.setMode('quiz');
          }}
          className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
            active === s
              ? 'bg-primary-500 text-white shadow-2'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
        >
          {SECTION_LABELS[s]}
        </button>
      ))}
    </div>
  );
}

function QuizModePicker() {
  const currentQuizMode = signals.currentQuizMode.value;
  const deck = signals.deck.value;
  const modes = quizRegistry.list({
    level: signals.currentLevel.value,
    cards: deck
  });

  return (
    <div className="flex flex-wrap gap-2 mb-6 justify-center">
      {modes.map(mode => (
        <button
          key={mode.id}
          type="button"
          onClick={() => signals.setQuizMode(mode.id)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            mode.id === currentQuizMode
              ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
              : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
          }`}
          aria-pressed={mode.id === currentQuizMode}
        >
          {mode.icon && <span className="mr-1">{mode.icon}</span>}
          {mode.label}
        </button>
      ))}
    </div>
  );
}

function QuizArea() {
  const currentQuizMode = signals.currentQuizMode.value;
  const card = signals.currentCard.value;
  const deck = signals.deck.value;

  const mode = quizRegistry.get(currentQuizMode);
  if (!mode) {
    return (
      <div className="card text-center py-16 text-slate-500">
        Quiz mode <code>{currentQuizMode}</code> is not registered.
      </div>
    );
  }

  if (!card) {
    return (
      <div className="card text-center py-16 text-slate-500">
        Loading deck…
      </div>
    );
  }

  const QuizComponent = mode.component;
  return (
    <QuizComponent
      card={card}
      deck={{ level: signals.currentLevel.value, cards: deck }}
      onAnswer={(_correct: boolean) => {
        // Quiz mode itself drives signals.updateWordSRS; this callback is
        // for any additional quiz-level state. v1: no-op.
      }}
    />
  );
}

export function App() {
  const level = signals.currentLevel.value;
  const section = currentSection.value;
  const streak = streakCount.value;
  const notifications = signals.notifications.value;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-1 border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gradient-primary">CEFR English</h1>
            <span className="hidden sm:inline text-xs text-slate-500 font-medium uppercase tracking-wide">
              Practice
            </span>
          </div>

          {/* `relative` anchors the OnboardingHint's absolute squiggle under
              the AccountMenu avatar. AccountMenu + OnboardingHint self-gate on
              VITE_FEATURE_CLOUD_SYNC, so they render nothing when sync is off. */}
          <div className="relative flex items-center gap-2">
            {streak > 0 && (
              <div
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-800 font-semibold"
                title={`${streak}-day streak`}
              >
                <span aria-hidden="true">🔥</span>
                <span>{streak}</span>
              </div>
            )}
            {SELECTABLE_L1S.length > 1 && (
              <select
                className="text-sm bg-transparent border border-[var(--color-border-soft)] rounded px-2 py-1"
                value={learnerLanguage.value}
                onChange={e => { learnerLanguage.value = e.currentTarget.value as typeof learnerLanguage.value; }}
                aria-label="Learner language"
              >
                {SELECTABLE_L1S.map(code => (
                  <option key={code} value={code}>{L1_META[code].flag} {code}</option>
                ))}
              </select>
            )}
            <AccountMenu />
            <OnboardingHint />
          </div>
        </div>
      </header>

      {/* Level selector */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-2 flex gap-1.5 overflow-x-auto">
          {ENGLISH_LEVELS.map(lvl => (
            <button
              key={lvl}
              type="button"
              onClick={() => signals.setLevel(lvl)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                lvl === level
                  ? 'bg-primary-500 text-white shadow-1'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              aria-pressed={lvl === level}
            >
              {lvl}
            </button>
          ))}
        </div>
      </nav>

      {/* Main */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">
        <SectionTabs />

        {section === 'flashcards' && (
          <StudyMode<EnglishCardExt>
            signals={signals}
            onPlayAudio={playAudio}
            renderBack={card => <EnglishCardBack card={card} />}
            keyboardHint={
              <span className="hidden sm:inline">
                Keyboard: Space/Enter (flip) • ←/→ (navigate) • 1-4 (quiz answer)
              </span>
            }
          />
        )}

        {section === 'quiz' && (
          <>
            <QuizModePicker />
            {/* Toggle exposed at B1 only — A1/A2 force JP on via the
                quizL1Subtext rule. Do not unwrap this level gate without
                updating quizL1Subtext in lockstep. */}
            {level === 'B1' && learnerLanguage.value === 'ja' && <L1QuizToggle />}
            <QuizArea />
          </>
        )}

        {section === 'situations' && (
          <SituationsBrowser
            signals={situationsSignals}
            mainLevelSetter={signals.setLevel}
            onPlayAudio={playAudio}
            activeCategory={activeSituationCategory.value}
            setActiveCategory={cat => {
              activeSituationCategory.value = cat;
            }}
          />
        )}

      </main>

      {/* Footer */}
      <footer className="text-center text-xs text-slate-400 py-4 px-4">
        Streaks reset when private browsing ends. Progress stored locally.
      </footer>

      <ToastContainer
        notifications={notifications}
        onDismiss={signals.dismissNotification}
      />
    </div>
  );
}
